import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { kitsAPI, schoolsAPI, productsAPI } from '../../utils/api';
import { LoadingSpinner } from '../../components/common';

const EMPTY_FORM = { name: '', school_id: '', grade: '', gender: 'all', description: '', full_kit_price: '', is_active: 1 };

const AdminKits = () => {
  const [kits, setKits] = useState([]);
  const [schools, setSchools] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [kitItems, setKitItems] = useState([]); // [{product_id, default_qty, is_optional}]
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copying, setCopying] = useState(false);
  const [xlsxWarnings, setXlsxWarnings] = useState([]);
  const [pasteText, setPasteText] = useState('');
  const [pasteResult, setPasteResult] = useState([]); // [{label, qty}] — only unmatched
  const [showPaste, setShowPaste] = useState(false);
  const [savedUnmatched, setSavedUnmatched] = useState([]); // persisted across edits
  const fileInputRef = useRef(null);
  const [filterSchool, setFilterSchool] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const load = () => {
    setLoading(true);
    Promise.all([kitsAPI.getAll({ includeInactive: true }), schoolsAPI.getAll(), productsAPI.getAll({ limit: 200 })])
      .then(([kRes, sRes, pRes]) => {
        setKits(kRes.data);
        setSchools(sRes.data);
        setProducts(pRes.data.products);
      }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openNew = () => {
    setForm(EMPTY_FORM); setEditing(null); setCopying(false); setKitItems([]); setShowForm(true); setError(''); setXlsxWarnings([]); setPasteText(''); setPasteResult([]); setShowPaste(false); setSavedUnmatched([]);
  };

  const parsePasteList = () => {
    const lines = pasteText.split('\n').map(l => l.trim()).filter(Boolean);
    const results = lines.map(line => {
      // Formats: "Name, Qty"  |  "Name - Qty"  |  "Qty x Name"  |  "Name Qty"(trailing number)
      let name = line, qty = 1;
      const commaMatch = line.match(/^(.+?),\s*(\d+)\s*$/);
      const dashMatch = line.match(/^(.+?)\s+-\s+(\d+)\s*$/);
      const xPrefixMatch = line.match(/^(\d+)\s*[xX]\s+(.+)$/);
      const trailingNumMatch = line.match(/^(.+?)\s+(\d+)\s*$/);
      if (commaMatch) { name = commaMatch[1].trim(); qty = parseInt(commaMatch[2]); }
      else if (dashMatch) { name = dashMatch[1].trim(); qty = parseInt(dashMatch[2]); }
      else if (xPrefixMatch) { qty = parseInt(xPrefixMatch[1]); name = xPrefixMatch[2].trim(); }
      else if (trailingNumMatch) { name = trailingNumMatch[1].trim(); qty = parseInt(trailingNumMatch[2]); }

      const lower = name.toLowerCase();
      // 1. Exact name match, 2. Exact SKU match, 3. Name contains search, 4. Search contains name
      let product =
        products.find(p => p.name.toLowerCase() === lower) ||
        products.find(p => p.sku && p.sku.toLowerCase() === lower) ||
        products.find(p => p.name.toLowerCase().includes(lower)) ||
        products.find(p => lower.includes(p.name.toLowerCase()));
      return { label: name, qty, product, matched: !!product };
    });
    // Immediately add matched items as kit item rows
    const matched = results.filter(r => r.matched);
    if (matched.length) {
      setKitItems(prev => {
        const existing = new Set(prev.map(i => String(i.product_id)));
        const newItems = matched
          .filter(r => !existing.has(String(r.product.id)))
          .map(r => ({ product_id: r.product.id, default_qty: r.qty, is_optional: 0, colors: r.product.colors || '' }));
        return [...prev, ...newItems];
      });
      // Auto-dismiss any savedUnmatched labels that now match
      setSavedUnmatched(prev => {
        const next = prev.filter(u => {
          const ul = u.label.toLowerCase();
          return !matched.some(r => {
            const pl = r.product.name.toLowerCase();
            return ul === pl || ul.includes(pl) || pl.includes(ul) || (r.product.sku && ul === r.product.sku.toLowerCase());
          });
        });
        if (next.length !== prev.length && editing) {
          if (next.length > 0) localStorage.setItem('kit_unmatched_' + editing, JSON.stringify(next));
          else localStorage.removeItem('kit_unmatched_' + editing);
        }
        return next;
      });
    }
    // Only keep unmatched in pasteResult to show as warnings
    setPasteResult(results.filter(r => !r.matched));
    if (results.every(r => r.matched)) { setPasteText(''); setShowPaste(false); }
  };

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const kitSheet = XLSX.utils.aoa_to_sheet([
      ['Kit Name *', 'School Name *', 'Grade *', 'Gender (all/boy/girl)', 'Full Kit Price', 'Description'],
      ['Example Kit Name', 'Example School Name', '3', 'all', '45.99', 'Optional description here'],
    ]);
    kitSheet['!cols'] = [{ wch: 25 }, { wch: 25 }, { wch: 8 }, { wch: 20 }, { wch: 16 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, kitSheet, 'Kit Info');

    const prodSheet = XLSX.utils.aoa_to_sheet([
      ['Product SKU *', 'Product Name (reference only)', 'Qty *', 'Optional (yes/no)'],
      ['SKU001', 'Pencil Set 12pk', '2', 'no'],
      ['SKU002', '30cm Ruler', '1', 'yes'],
    ]);
    prodSheet['!cols'] = [{ wch: 18 }, { wch: 30 }, { wch: 8 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, prodSheet, 'Products');
    XLSX.writeFile(wb, 'school-kit-template.xlsx');
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(new Uint8Array(evt.target.result), { type: 'array' });
        const warnings = [];

        // --- Kit Info sheet ---
        const kitSheetName = wb.SheetNames.find(n => n.toLowerCase().includes('kit')) || wb.SheetNames[0];
        const kitRows = XLSX.utils.sheet_to_json(wb.Sheets[kitSheetName], { header: 1, defval: '' });
        const kitRow = kitRows[1]; // row index 1 = data row
        if (!kitRow || !kitRow[0]) { setError('Invalid Excel: Kit Info sheet must have data in row 2.'); return; }
        const [kitName, schoolName, grade, gender, fullKitPrice, description] = kitRow;

        // Match school by name (case-insensitive)
        const matchedSchool = schools.find(s => s.name.toLowerCase().trim() === String(schoolName || '').toLowerCase().trim());
        if (!matchedSchool) warnings.push(`School "${schoolName}" not found — please select manually`);

        // --- Products sheet ---
        const prodSheetName = wb.SheetNames.find(n => n.toLowerCase().includes('product')) || wb.SheetNames[1];
        const parsedItems = [];
        if (prodSheetName && wb.Sheets[prodSheetName]) {
          const prodRows = XLSX.utils.sheet_to_json(wb.Sheets[prodSheetName], { header: 1, defval: '' });
          for (let i = 1; i < prodRows.length; i++) {
            const row = prodRows[i];
            const sku = String(row[0] || '').trim();
            if (!sku) continue;
            const matchedProd = products.find(p => p.sku && p.sku.toLowerCase() === sku.toLowerCase());
            if (!matchedProd) { warnings.push(`Row ${i + 1}: SKU "${sku}" not found — skipped`); continue; }
            parsedItems.push({
              product_id: matchedProd.id,
              default_qty: parseInt(row[2]) || 1,
              is_optional: String(row[3] || '').toLowerCase() === 'yes' ? 1 : 0,
              colors: matchedProd.colors || '',
            });
          }
        }

        // Populate form
        const genderVal = ['all', 'boy', 'girl'].includes(String(gender).toLowerCase()) ? String(gender).toLowerCase() : 'all';
        setForm({ name: kitName || '', school_id: matchedSchool?.id || '', grade: String(grade || ''), gender: genderVal, full_kit_price: fullKitPrice || '', description: description || '', is_active: 1 });
        setKitItems(parsedItems);
        setXlsxWarnings(warnings);
        setEditing(null); setCopying(false); setShowForm(true); setError('');
      } catch (err) {
        setError('Failed to parse Excel file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = ''; // reset so same file can be re-uploaded
  };

  const openEdit = async (kit) => {
    const res = await kitsAPI.getById(kit.id);
    const k = res.data;
    setForm({ name: k.name, school_id: k.school_id, grade: k.grade, gender: k.gender, description: k.description || '', full_kit_price: k.full_kit_price || '', is_active: k.is_active });
    setKitItems(k.items.map(i => ({ product_id: i.product_id, default_qty: i.default_qty, is_optional: i.is_optional || 0, colors: i.colors || '' })));
    const stored = JSON.parse(localStorage.getItem('kit_unmatched_' + kit.id) || '[]');
    setSavedUnmatched(stored);
    setEditing(kit.id); setCopying(false); setShowForm(true); setError(''); setPasteText(''); setPasteResult([]); setShowPaste(false);
  };

  const openCopy = async (kit) => {
    const res = await kitsAPI.getById(kit.id);
    const k = res.data;
    setForm({ name: `Copy of ${k.name}`, school_id: k.school_id, grade: k.grade, gender: k.gender, description: k.description || '', full_kit_price: k.full_kit_price || '', is_active: 1 });
    setKitItems(k.items.map(i => ({ product_id: i.product_id, default_qty: i.default_qty, is_optional: i.is_optional || 0, colors: i.colors || '' })));
    setEditing(null); setCopying(true); setShowForm(true); setError(''); setPasteText(''); setPasteResult([]); setShowPaste(false); setSavedUnmatched([]);
  };

  const handleSave = async () => {
    if (!form.name || !form.school_id || !form.grade) { setError('Name, School, and Grade are required'); return; }
    setSaving(true); setError('');
    try {
      // Deduplicate by product_id — keep the last occurrence (most recently added)
      const seen = new Map();
      kitItems.forEach(item => seen.set(String(item.product_id), item));
      const deduped = Array.from(seen.values());
      const payload = { ...form, items: deduped };
      if (editing) {
        await kitsAPI.update(editing, payload);
        // Merge new unmatched from paste with existing saved unmatched
        const merged = [...savedUnmatched];
        pasteResult.forEach(r => { if (!merged.find(m => m.label.toLowerCase() === r.label.toLowerCase())) merged.push({ label: r.label, qty: r.qty }); });
        if (merged.length > 0) localStorage.setItem('kit_unmatched_' + editing, JSON.stringify(merged));
        else localStorage.removeItem('kit_unmatched_' + editing);
      } else {
        const res = await kitsAPI.create(payload);
        const newId = res.data?.id;
        if (newId && pasteResult.length > 0) {
          localStorage.setItem('kit_unmatched_' + newId, JSON.stringify(pasteResult.map(r => ({ label: r.label, qty: r.qty }))));
        }
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const dismissUnmatched = (idx) => {
    setSavedUnmatched(prev => {
      const next = prev.filter((_, i) => i !== idx);
      if (editing) {
        if (next.length > 0) localStorage.setItem('kit_unmatched_' + editing, JSON.stringify(next));
        else localStorage.removeItem('kit_unmatched_' + editing);
      }
      return next;
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this kit permanently? This cannot be undone.')) return;
    try {
      await kitsAPI.delete(id);
      load();
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const addKitItem = () => {
    setKitItems(prev => [...prev, { product_id: '', default_qty: 1, is_optional: 0, colors: '' }]);
  };

  const autoDismissUnmatched = (prod) => {
    if (!prod) return;
    setSavedUnmatched(prev => {
      const pl = prod.name.toLowerCase();
      const next = prev.filter(u => {
        const ul = u.label.toLowerCase();
        return !(ul === pl || ul.includes(pl) || pl.includes(ul) || (prod.sku && ul === prod.sku.toLowerCase()));
      });
      if (next.length !== prev.length) {
        if (editing) {
          if (next.length > 0) localStorage.setItem('kit_unmatched_' + editing, JSON.stringify(next));
          else localStorage.removeItem('kit_unmatched_' + editing);
        }
      }
      return next;
    });
  };

  const updateKitItem = (idx, key, val) => {
    if (key === 'product_id') {
      const prod = products.find(p => String(p.id) === String(val));
      setKitItems(prev => prev.map((item, i) => i === idx ? { ...item, product_id: val, colors: prod?.colors || '' } : item));
      autoDismissUnmatched(prod);
    } else {
      setKitItems(prev => prev.map((item, i) => i === idx ? { ...item, [key]: val } : item));
    }
  };

  const toggleKitItemColor = (idx, color) => {
    const item = kitItems[idx];
    const current = item.colors ? item.colors.split(',').map(c => c.trim()).filter(Boolean) : [];
    const next = current.includes(color) ? current.filter(c => c !== color) : [...current, color];
    updateKitItem(idx, 'colors', next.join(', '));
  };

  const removeKitItem = (idx) => {
    setKitItems(prev => prev.filter((_, i) => i !== idx));
  };

  const selectedSchool = schools.find(s => s.id === parseInt(form.school_id));
  const availableGrades = selectedSchool?.grades_supported?.split(',').map(g => g.trim()) || [];

  const toggleSort = (col) => { setSortDir(sortCol === col && sortDir === 'asc' ? 'desc' : 'asc'); setSortCol(col); };
  const si = (col) => <span style={{ fontSize: '0.65rem', marginLeft: 2, opacity: sortCol === col ? 1 : 0.3 }}>{sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>;

  const filteredKits = kits
    .filter(k => {
      const matchSchool = !filterSchool || String(k.school_id) === String(filterSchool);
      const q = searchQuery.toLowerCase();
      const matchSearch = !q || k.name.toLowerCase().includes(q) || (k.school_name || '').toLowerCase().includes(q) || String(k.grade).includes(q);
      return matchSchool && matchSearch;
    })
    .sort((a, b) => {
      const va = a[sortCol] ?? ''; const vb = b[sortCol] ?? '';
      const n = parseFloat(va), m = parseFloat(vb);
      const cmp = !isNaN(n) && !isNaN(m) ? n - m : String(va).toLowerCase().localeCompare(String(vb).toLowerCase());
      return sortDir === 'desc' ? -cmp : cmp;
    });

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div><h1>School Kits</h1><p>{kits.length} kits</p></div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-outline btn-sm" onClick={downloadTemplate} title="Download Excel template">⬇ Template</button>
          <button className="btn btn-outline btn-sm" style={{ color: '#059669', borderColor: '#059669' }} onClick={() => fileInputRef.current?.click()} title="Import kit from Excel">📊 Import Excel</button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleExcelUpload} />
          <button className="btn btn-primary" onClick={openNew}>+ Create Kit</button>
        </div>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '4px solid #2563eb' }}>
          <h2 style={{ fontWeight: 700, marginBottom: '1rem' }}>{copying ? 'Copy Kit' : editing ? 'Edit Kit' : 'New Kit'}</h2>
          {error && <div className="alert alert-error">{error}</div>}
          {xlsxWarnings.length > 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '0.75rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#92400e', marginBottom: '0.25rem' }}>⚠ Import warnings — please review:</div>
              {xlsxWarnings.map((w, i) => <div key={i} style={{ fontSize: '0.8rem', color: '#b45309' }}>• {w}</div>)}
            </div>
          )}

          <div className="grid-2">
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Kit Name *</label>
              <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. DPS Grade 3 Kit" />
            </div>
            <div className="form-group">
              <label className="form-label">School *</label>
              <select className="form-select w-full" value={form.school_id} onChange={e => { set('school_id', e.target.value); set('grade', ''); }}>
                <option value="">-- Select School --</option>
                {schools.map(s => <option key={s.id} value={s.id}>{s.name}{(s.city || s.state) ? ` — ${[s.city, s.state].filter(Boolean).join(', ')}` : ''}</option>)}
              </select>
              {selectedSchool && (
                <div style={{ marginTop: '0.3rem', fontSize: '0.78rem', color: '#4b5563', padding: '0.3rem 0.6rem', background: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd' }}>
                  📍 {[selectedSchool.address, selectedSchool.city, selectedSchool.state, selectedSchool.pincode].filter(Boolean).join(', ') || 'No address on file'}
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Grade *</label>
              <select className="form-select w-full" value={form.grade} onChange={e => set('grade', e.target.value)} disabled={!form.school_id}>
                <option value="">-- Select Grade --</option>
                {availableGrades.map(g => <option key={g} value={g}>Grade {g}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Gender</label>
              <select className="form-select w-full" value={form.gender} onChange={e => set('gender', e.target.value)}>
                <option value="all">All</option>
                <option value="boy">Boy</option>
                <option value="girl">Girl</option>
              </select>
            </div>
            {editing && (
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select w-full" value={form.is_active} onChange={e => set('is_active', e.target.value)}>
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Full Kit Price <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 400 }}>(optional — bundle discount price)</span></label>
              <input className="form-input" type="number" min="0" step="0.01" value={form.full_kit_price} onChange={e => set('full_kit_price', e.target.value)} placeholder="e.g. 45.99" />
            </div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Description</label>
              <textarea className="form-input" rows={2} value={form.description} onChange={e => set('description', e.target.value)} />
            </div>
          </div>

          {/* Kit Items */}
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', marginTop: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ fontWeight: 700 }}>Kit Items ({kitItems.length})</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-outline btn-sm" style={{ color: '#7c3aed', borderColor: '#7c3aed' }} onClick={() => { setShowPaste(p => !p); setPasteResult([]); }}>
                  {showPaste ? '✕ Close Paste' : '📋 Paste List'}
                </button>
                <button className="btn btn-outline btn-sm" onClick={addKitItem}>+ Add Item</button>
              </div>
            </div>

            {/* Paste List Box */}
            {showPaste && (
              <div style={{ background: '#faf5ff', border: '1px solid #c4b5fd', borderRadius: 10, padding: '0.9rem 1rem', marginBottom: '0.9rem' }}>
                <div style={{ fontSize: '0.8rem', color: '#6d28d9', fontWeight: 600, marginBottom: '0.35rem' }}>Paste items — one per line</div>
                <div style={{ fontSize: '0.75rem', color: '#7c3aed', marginBottom: '0.5rem', opacity: 0.85 }}>
                  Formats: <code>Item Name, Qty</code> &nbsp;|&nbsp; <code>Item Name - Qty</code> &nbsp;|&nbsp; <code>2 x Item Name</code> &nbsp;|&nbsp; <code>Item Name 2</code>
                </div>
                <textarea
                  className="form-input"
                  rows={5}
                  style={{ fontFamily: 'monospace', fontSize: '0.82rem', marginBottom: '0.5rem' }}
                  placeholder={'Pencil Set 12pk, 2\n30cm Ruler, 1\nNotepad A4 - 3\n2 x Eraser'}
                  value={pasteText}
                  onChange={e => { setPasteText(e.target.value); setPasteResult([]); }}
                />
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className="btn btn-sm" style={{ background: '#7c3aed', color: '#fff', border: 'none' }} onClick={parsePasteList} disabled={!pasteText.trim()}>Match Products</button>
                  {pasteResult.length > 0 && (
                    <button className="btn btn-outline btn-sm" onClick={() => { setPasteText(''); setPasteResult([]); }}>Clear</button>
                  )}
                </div>

                {pasteResult.length > 0 && (
                  <div style={{ marginTop: '0.65rem', borderTop: '1px solid #ddd6fe', paddingTop: '0.65rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#b45309', fontWeight: 600, marginBottom: '0.3rem' }}>⚠ Not found — update the text and try again:</div>
                    {pasteResult.map((r, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem', fontSize: '0.82rem' }}>
                        <span style={{ color: '#ef4444', fontWeight: 700 }}>✕</span>
                        <span style={{ color: '#374151' }}>{r.label}</span>
                        <span style={{ color: '#ef4444', fontSize: '0.78rem', fontWeight: 600 }}>(Product not available)</span>
                        <span style={{ color: '#6b7280' }}>— qty: {r.qty}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {kitItems.length === 0 && (
              <p className="text-muted text-sm" style={{ marginBottom: '0.75rem' }}>No items yet. Click "+ Add Item" to add products to this kit.</p>
            )}

            {kitItems.map((item, idx) => (
              <div key={idx} style={{ marginBottom: '0.5rem', background: '#f9fafb', padding: '0.5rem 0.75rem', borderRadius: 8 }}>
                {/* Row 1: product + controls */}
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    className="form-select"
                    style={{ flex: 2, minWidth: 160 }}
                    value={item.product_id}
                    onChange={e => updateKitItem(idx, 'product_id', e.target.value)}
                  >
                    <option value="">-- Select Product --</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name} (${p.price})</option>)}
                  </select>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <label style={{ fontSize: '0.78rem', color: '#6b7280', whiteSpace: 'nowrap' }}>Qty:</label>
                    <input
                      type="number"
                      min={1}
                      className="form-input"
                      style={{ width: 60 }}
                      value={item.default_qty}
                      onChange={e => updateKitItem(idx, 'default_qty', parseInt(e.target.value) || 1)}
                    />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={!!item.is_optional} onChange={e => updateKitItem(idx, 'is_optional', e.target.checked ? 1 : 0)} />
                    Optional
                  </label>
                  <button
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '1.1rem', flexShrink: 0 }}
                    onClick={() => removeKitItem(idx)}
                    title="Remove"
                  >✕</button>
                </div>
                {/* Row 2: selectable color chips */}
                {(() => {
                  const prod = products.find(p => String(p.id) === String(item.product_id));
                  const productColors = prod?.colors ? prod.colors.split(',').map(c => c.trim()).filter(Boolean) : [];
                  if (!productColors.length) return null;
                  const selected = item.colors ? item.colors.split(',').map(c => c.trim()).filter(Boolean) : [];
                  return (
                    <div style={{ marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <label style={{ fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'nowrap' }}>Color options:</label>
                      {productColors.map(c => {
                        const on = selected.includes(c);
                        return (
                          <button
                            key={c}
                            type="button"
                            onClick={() => toggleKitItemColor(idx, c)}
                            style={{
                              borderRadius: 999, padding: '0.1rem 0.55rem',
                              fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                              border: on ? '2px solid #2563eb' : '2px solid #d1d5db',
                              background: on ? '#dbeafe' : '#f3f4f6',
                              color: on ? '#1e40af' : '#6b7280',
                            }}
                          >{c}</button>
                        );
                      })}
                      {selected.length === 0 && <span style={{ fontSize: '0.68rem', color: '#f59e0b', fontWeight: 600 }}>No options selected</span>}
                    </div>
                  );
                })()}
              </div>
            ))}

            {/* Persistent unmatched items from paste */}
            {savedUnmatched.length > 0 && (
              <div style={{ marginTop: '0.75rem', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 8, padding: '0.75rem 0.9rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#92400e' }}>⚠ Not yet in product catalog ({savedUnmatched.length})</span>
                  <button className="btn btn-outline btn-sm" style={{ fontSize: '0.72rem', padding: '0.1rem 0.55rem' }} onClick={() => { setSavedUnmatched([]); if (editing) localStorage.removeItem('kit_unmatched_' + editing); }}>Dismiss all</button>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#92400e', marginBottom: '0.5rem' }}>
                  Add these to <strong>Admin → Products</strong> first, then come back and use <strong>+ Add Item</strong> or <strong>📋 Paste List</strong> to include them.
                </div>
                {savedUnmatched.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', background: '#fef3c7', borderRadius: 5, padding: '0.25rem 0.5rem' }}>
                    <span style={{ color: '#ef4444', fontWeight: 700, fontSize: '0.85rem' }}>✕</span>
                    <span style={{ flex: 1, fontWeight: 600, fontSize: '0.82rem', color: '#374151' }}>{item.label}</span>
                    <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>qty: {item.qty}</span>
                    <span style={{ fontSize: '0.72rem', color: '#ef4444', fontWeight: 600 }}>(Product not available)</span>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '0.9rem', padding: '0 0.2rem', lineHeight: 1 }}
                      onClick={() => dismissUnmatched(i)}
                      title="Dismiss"
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2" style={{ marginTop: '1rem' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Kit'}</button>
            <button className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {!loading && (
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <input
            className="form-input"
            style={{ flex: 1, minWidth: 180, maxWidth: 280 }}
            placeholder="Search by name, school, grade…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <select
            className="form-select"
            style={{ minWidth: 180 }}
            value={filterSchool}
            onChange={e => setFilterSchool(e.target.value)}
          >
            <option value="">All Schools</option>
            {schools.map(s => <option key={s.id} value={s.id}>{s.name}{(s.city || s.state) ? ` — ${[s.city, s.state].filter(Boolean).join(', ')}` : ''}</option>)}
          </select>
          {(filterSchool || searchQuery) && (
            <button className="btn btn-outline btn-sm" onClick={() => { setFilterSchool(''); setSearchQuery(''); }}>Clear</button>
          )}
          <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>{filteredKits.length} of {kits.length} kits</span>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('name')}>Kit Name {si('name')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('school_name')}>School {si('school_name')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('grade')}>Grade {si('grade')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('gender')}>Gender {si('gender')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('item_count')}>Items {si('item_count')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('is_active')}>Status {si('is_active')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredKits.map(k => (
                  <tr key={k.id}>
                    <td style={{ fontWeight: 600 }}>{k.name}</td>
                    <td style={{ fontSize: '0.85rem' }}>{k.school_name}</td>
                    <td>Grade {k.grade}</td>
                    <td>{k.gender === 'boy' ? 'Boy' : k.gender === 'girl' ? 'Girl' : 'All'}</td>
                    <td>{k.item_count || 0} items</td>
                    <td>
                      <span className={`badge ${k.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {k.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button className="btn btn-outline btn-sm" onClick={() => openEdit(k)}>Edit</button>
                        <button className="btn btn-outline btn-sm" style={{ color: '#2563eb', borderColor: '#2563eb' }} onClick={() => openCopy(k)} title="Duplicate this kit">Copy</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(k.id)}>Del</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredKits.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>{kits.length === 0 ? 'No kits yet. Create your first kit!' : 'No kits match the current filters.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminKits;
